import { i18n } from '@/plugins/i18n';
import { TransStatement, PayChannelSuccessRate, BetHistoryEntity } from './api/model';
import { OrderType, PaySource, TransactionType } from './config';
import { ChatWarnings } from '@/store/chatWarning/chatWarning.types';
import { getChatWarning } from './chatWarning.utils';
import { ChatWarning } from './types';
import { formatMoney } from './utils';
import { store } from '@/store';
import { logger } from '@/common/api/logger';
import { switcher } from './bot/flow-frameworks/switcher';
import { ExternalTransactionKind, ExternalTransactionType } from './bot/enum/ExternalTransactionType';

const DELAY_THRESHOLD = {
  BANK_DEPOSIT_DELAY_MINUTES: 60,
  CARD_DEPOSIT_DELAY_MINUTES: 10,
};

// Enum from Pay team
export const PAY_STATUS_ENUM = {
  INIT: 0,
  PROCESSING: 10,
  PENDING: 15,
  PENDING_REVIEW: 16,
  PAY_SUCC: 20,
  PAY_FAIL: 30,
  PAY_AUDIT_FAIL: 31,
  PAY_RISK_FAIL: 32,
  PAY_MANUAL_FAIL: 33,
  PAY_AUTO_FAIL: 34,
  PAY_UNKNOWN: 90,
};

export const TRANSACTION_TYPE_NAME_ENUM = {
  [TransactionType.Deposit]: 'Deposit',
  [TransactionType.Withdrawal]: 'Withdrawal',
  [TransactionType.Bet]: 'Bet',
} as const;

export const TRANSACTION_PREPOSITION_ENUM = {
  [TransactionType.Deposit]: 'from',
  [TransactionType.Withdrawal]: 'to',
  [TransactionType.Bet]: 'of',
} as const;

export const TRANSACTION_STATUS_ENUM = {
  PENDING: [PAY_STATUS_ENUM.PROCESSING, PAY_STATUS_ENUM.PENDING, PAY_STATUS_ENUM.PENDING_REVIEW],
  SUCCESSFUL: [PAY_STATUS_ENUM.PAY_SUCC],
  FAILED: [PAY_STATUS_ENUM.PAY_FAIL, PAY_STATUS_ENUM.PAY_AUDIT_FAIL, PAY_STATUS_ENUM.PAY_RISK_FAIL],
  UNKNOWN: [PAY_STATUS_ENUM.PAY_UNKNOWN],
} as const;

export const TRANSACTION_STATUS_NAME_ENUM = {
  [PAY_STATUS_ENUM.PROCESSING]: 'Pending',
  [PAY_STATUS_ENUM.PENDING]: 'Pending',
  [PAY_STATUS_ENUM.PENDING_REVIEW]: 'Pending',

  [PAY_STATUS_ENUM.PAY_SUCC]: 'Successful',

  [PAY_STATUS_ENUM.PAY_FAIL]: 'Failed',
  [PAY_STATUS_ENUM.PAY_AUDIT_FAIL]: 'Failed',
  [PAY_STATUS_ENUM.PAY_RISK_FAIL]: 'Failed',
  [PAY_STATUS_ENUM.PAY_MANUAL_FAIL]: 'Failed',
  [PAY_STATUS_ENUM.PAY_AUTO_FAIL]: 'Failed',

  [PAY_STATUS_ENUM.PAY_UNKNOWN]: 'Unknown',
} as const;

export enum UPDATED_STATUS_WARNING_VARIANTS {
  WARNING = 'warning',
  SUCCESSFUL = 'successful',
  PROMINENT = 'prominent',
}

export type TransactionStatus = (typeof PAY_STATUS_ENUM)[keyof typeof PAY_STATUS_ENUM];
export type TransactionTypeName = (typeof TRANSACTION_TYPE_NAME_ENUM)[keyof typeof TRANSACTION_TYPE_NAME_ENUM];
export type TransactionStatusName = (typeof TRANSACTION_STATUS_NAME_ENUM)[keyof typeof TRANSACTION_STATUS_NAME_ENUM];
export type UpdatedStatusWarningVariant =
  (typeof UPDATED_STATUS_WARNING_VARIANTS)[keyof typeof UPDATED_STATUS_WARNING_VARIANTS];

const SELECTION_EVENT_STATUS = {
  FINISHED: 4,
};

const TOURNAMENT_ID = {
  SERIES_D: 'sr:tournament:39221',
};

const BANK_PAY_CH_IDS = [23, 25];

const REMEDY_SUPPORT_BANK_PAY_CH_IDS = [21, 23, 25];

export const WINNING_STATUS_ENUM = {
  RUNNING: 0,
  PARTIAL_WIN: 5,
  WON: 20,
  LOST: 30,
  VOID: 40,
  PENDING: 90,
} as const;

export const BET_STATUS_NAME_ENUM = {
  0: 'Running',
  5: 'Partial Win',
  20: 'Won',
  30: 'Lost',
  40: 'Void',
  90: 'Pending',
} as const;

export type BetStatus = (typeof WINNING_STATUS_ENUM)[keyof typeof WINNING_STATUS_ENUM];

export function checkTransactionWarning({
  transaction,
  transactionMethodSuccessRate,
  type: transactionType,
  chatWarnings,
}: {
  transactionMethodSuccessRate?: PayChannelSuccessRate[] | null;
  transaction: TransStatement;
  type: TransactionType;
  chatWarnings: ChatWarnings;
}): ChatWarning {
  const hasPaymentProviderIssue = getTransactionProviderHasIssue(transaction, transactionMethodSuccessRate);

  if (hasPaymentProviderIssue) {
    // If it's provider issue
    if (transaction.counterAuthority) {
      return getChatWarning({
        chatWarnings,
        warningName: 'paymentProviderIssue',
        warningTextVariables: { provider: `<strong>${transaction.counterAuthority}</strong>` },
        defaultTextLocale: 'page.issue.transaction.payment_method_issue.provider',
        kind: 'prominent',
      });
    }

    // Deposit payment method issue
    if (transactionType === TransactionType.Deposit) {
      return getChatWarning({
        chatWarnings,
        warningName: 'paymentDepositIssue',
        defaultTextLocale: 'page.issue.transaction.payment_method_issue.deposit',
        kind: 'prominent',
      });
    }

    // Withdrawal payment method issue
    if (transactionType === TransactionType.Withdrawal) {
      return getChatWarning({
        chatWarnings,
        warningName: 'paymentWithdrawalIssue',
        defaultTextLocale: 'page.issue.transaction.payment_method_issue.withdrawal',
        kind: 'prominent',
      });
    }
  }

  if (transactionType === TransactionType.Deposit) {
    return getTransactionWarning({ transaction, chatWarnings });
  }

  return { show: false, message: '' };
}

export function getTransactionWarning({
  transaction,
  chatWarnings,
}: {
  transaction: TransStatement;
  chatWarnings: ChatWarnings;
}): ChatWarning {
  const isCard = isCardTransaction(transaction);
  const isBank = isBankTransaction(transaction);

  const possibleDelay = isCard
    ? DELAY_THRESHOLD.CARD_DEPOSIT_DELAY_MINUTES
    : isBank
      ? DELAY_THRESHOLD.BANK_DEPOSIT_DELAY_MINUTES
      : 0;
  const isRecentlyCreated = transaction.createTime > +new Date() - possibleDelay * 60 * 1000;
  const isStatusPending = isTxStatusPending(transaction.status);

  if (!isRecentlyCreated || !isStatusPending) {
    return { show: false, message: '' };
  }

  const warningTextVariables = {
    delay: `<strong>${i18n.t('page.issue.transaction.minutes_delay', { delay: possibleDelay })}</strong>`,
  };

  if (isCard) {
    return getChatWarning({
      warningName: 'cardTransfer',
      chatWarnings,
      warningTextVariables,
      defaultTextLocale: 'page.issue.transaction.pending_card_transfer',
    });
  }

  if (isBank) {
    return getChatWarning({
      warningName: 'bankTransfer',
      chatWarnings,
      warningTextVariables,
      defaultTextLocale: 'page.issue.transaction.pending_bank_transfer',
    });
  }

  return { show: false, message: '' };
}

function getTransactionProviderHasIssue(
  selectedTransaction: TransStatement,
  transactionMethodSuccessRate?: PayChannelSuccessRate[] | null
) {
  if (!transactionMethodSuccessRate?.length) return false;
  const showAlert = [selectedTransaction].some((transaction) =>
    transactionMethodSuccessRate.some((sr) => {
      if (sr.payChannelId !== transaction.payChId || !sr.networks?.length) return false;
      // when network === unknown, then we get only one record in the array.
      const isUnknown = sr.networks.length === 1 && (sr.networks[0]?.network || '').toUpperCase() === 'UNKNOWN';
      if (isUnknown) return sr.networks[0].displayAlert;
      return sr.networks.some((n) => n.network === transaction.network && n.displayAlert);
    })
  );

  return showAlert;
}

// This function is used to determine when to use "Card Transaction Warning"
const isCardTransaction = (transaction: TransStatement) => transaction.paySource === PaySource.CARD;

// This function is used to determine when to use "Bank Transaction Warning"
const isBankTransaction = (transaction: TransStatement) => BANK_PAY_CH_IDS.includes(transaction.payChId);

// This function is used to determine when to show "Remedy" for bank transactions
const isRemedySupportBankTransaction = (transaction: TransStatement) =>
  REMEDY_SUPPORT_BANK_PAY_CH_IDS.includes(transaction.payChId);

// This function is used to determine when to show "Remedy" for transactions
export const isTransactionSupportRemedy = (transaction: TransStatement) => {
  const isCard = isCardTransaction(transaction);
  const isBank = isRemedySupportBankTransaction(transaction);

  const possibleDelay = isCard
    ? DELAY_THRESHOLD.CARD_DEPOSIT_DELAY_MINUTES
    : isBank
      ? DELAY_THRESHOLD.BANK_DEPOSIT_DELAY_MINUTES
      : 0;
  const isRecentlyCreated = transaction.createTime > +new Date() - possibleDelay * 60 * 1000;
  const isStatusPending = isTxStatusPending(transaction.status);

  if (isRecentlyCreated || !isStatusPending) {
    return false;
  }

  return isCard || isBank;
};

export const isUnsettledBet = (transaction: BetHistoryEntity) =>
  transaction.winningStatus === WINNING_STATUS_ENUM.RUNNING ||
  transaction.winningStatus === WINNING_STATUS_ENUM.PENDING;

export function checkBetTransactionWarning({
  transaction,
  chatWarnings,
}: {
  transaction: BetHistoryEntity;
  chatWarnings: ChatWarnings;
}) {
  const seriesDTournament = transaction.selections.find(
    (selection) => selection.tournamentId === TOURNAMENT_ID.SERIES_D
  );

  const isSeriesDTournament = !!seriesDTournament;
  const isSeriesDTournamentFinished = seriesDTournament?.eventStatus === SELECTION_EVENT_STATUS.FINISHED;

  const isBetNotFinished = isUnsettledBet(transaction);

  if (isSeriesDTournament && isSeriesDTournamentFinished && isBetNotFinished) {
    return getChatWarning({
      chatWarnings,
      warningName: 'seriesD',
      defaultTextLocale: 'page.issue.transaction.tournament.series_d',
    });
  }

  return { show: false, message: '' };
}

export const isBetTransaction = (
  _transaction: TransStatement | BetHistoryEntity,
  transactionType: TransactionType
): _transaction is BetHistoryEntity => transactionType === TransactionType.Bet;

export const isTxStatusPending = (status: TransactionStatus) => TRANSACTION_STATUS_ENUM.PENDING.includes(status);
export const isTxStatusSuccessful = (status: TransactionStatus) => TRANSACTION_STATUS_ENUM.SUCCESSFUL.includes(status);
export const isTxStatusFailed = (status: TransactionStatus) => TRANSACTION_STATUS_ENUM.FAILED.includes(status);
export const isTxStatusUnknown = (status: TransactionStatus) => TRANSACTION_STATUS_ENUM.UNKNOWN.includes(status);

export const generateIssueMessage = (params: {
  type: TransactionType;
  transaction: TransStatement | BetHistoryEntity;
}) => {
  const { type, transaction } = params;
  const typeName = TRANSACTION_TYPE_NAME_ENUM[type];
  const preposition = TRANSACTION_PREPOSITION_ENUM[type];

  if (isBetTransaction(transaction, type)) {
    return `${OrderType[transaction.orderType] || ''} ${typeName} (Id: ${transaction.shortId})`;
  }

  const currency = store.getters['user/currency'] || '';
  const amount = formatMoney(transaction.amount);
  const authority = transaction.counterAuthority ? transaction.counterAuthority : transaction.counterFull;

  const msg = `${typeName} ${authority ? preposition : ''} ${authority || ''} ${amount} ${currency} (Id: ${
    transaction.tradeId
  })`;

  return msg;
};

const transactionTypeSwitch = switcher<TransactionType, ExternalTransactionKind[]>()
  .when(TransactionType.Deposit, () => [
    ExternalTransactionType.DEPOSITS_PENDING,
    ExternalTransactionType.DEPOSITS_FAILED,
    ExternalTransactionType.DEPOSITS_SUCCESSFUL,
  ])
  .when(TransactionType.Withdrawal, () => [
    ExternalTransactionType.WITHDRAWALS_PENDING,
    ExternalTransactionType.WITHDRAWALS_FAILED,
    ExternalTransactionType.WITHDRAWALS_SUCCESSFUL,
  ])
  .when(TransactionType.Bet, () => [
    ExternalTransactionType.BETS_LOST,
    ExternalTransactionType.BETS_UNSETTLED,
    ExternalTransactionType.BETS_VOIDED,
    ExternalTransactionType.BETS_WON,
  ])
  .none((type) => logger.error('[TRANSACTION_TYPE] Not identified', { type }));

const transactionSubtypeSwitch = switcher<ExternalTransactionKind, TransactionType>()
  .when(
    [
      ExternalTransactionType.DEPOSITS_PENDING,
      ExternalTransactionType.DEPOSITS_FAILED,
      ExternalTransactionType.DEPOSITS_SUCCESSFUL,
    ],
    () => TransactionType.Deposit
  )
  .when(
    [
      ExternalTransactionType.WITHDRAWALS_PENDING,
      ExternalTransactionType.WITHDRAWALS_FAILED,
      ExternalTransactionType.WITHDRAWALS_SUCCESSFUL,
    ],
    () => TransactionType.Withdrawal
  )
  .when(
    [
      ExternalTransactionType.BETS_LOST,
      ExternalTransactionType.BETS_UNSETTLED,
      ExternalTransactionType.BETS_VOIDED,
      ExternalTransactionType.BETS_WON,
    ],
    () => TransactionType.Bet
  )
  .none((type) => logger.error('[TRANSACTION_SUBTYPE] Not identified', { type }));

export const getExternalTransactionTypes = (transactionType: TransactionType) => {
  return transactionTypeSwitch.match(transactionType);
};

export const getTransactionTypeFromSubtype = (subtype: ExternalTransactionKind) => {
  return transactionSubtypeSwitch.match(subtype);
};

// Type guard to check if transaction is a payment transaction (TransStatement)
export function isPaymentTransaction(tx: TransStatement | BetHistoryEntity): tx is TransStatement {
  return 'tradeId' in tx;
}

// Type guard to check if transaction is a bet transaction (BetHistoryEntity)
export function isBetTransactionType(tx: TransStatement | BetHistoryEntity): tx is BetHistoryEntity {
  return 'orderId' in tx;
}

// Helper to get transaction ID regardless of type
export function getTransactionId(tx: TransStatement | BetHistoryEntity): string {
  if (isPaymentTransaction(tx)) {
    return tx.tradeId;
  }
  return tx.orderId;
}

export function getAiRequestTransactionId(tx: TransStatement | BetHistoryEntity): string {
  if (isPaymentTransaction(tx)) {
    return tx.tradeId;
  }

  return tx.shortId;
}
